import { useQueryClient } from '@tanstack/react-query'
import { GraphQLError } from 'graphql-request/dist/types'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import { useEffect, useState } from 'react'
import { useContext } from 'react'

import { useCreateResidentialListingOpenHouseInquiry } from '@unreserved-frontend-v2/api/hooks/use-create-residential-listing-open-house-inquiry'
import { Button } from '@unreserved-frontend-v2/ui/button/button'
import { ErrorContainer, FieldError } from '@unreserved-frontend-v2/ui/error-container/error-container'
import { Modal } from '@unreserved-frontend-v2/ui/modal/modal'
import { SchemaFormLite } from '@unreserved-frontend-v2/ui/schema-form/schema-form-lite'
import { SchemaFormValues } from '@unreserved-frontend-v2/ui/schema-form/types'
import { Spinner } from '@unreserved-frontend-v2/ui/spinner'

import { formSection, useErrorMessages } from './utils'
import { AttendOpenHouseFormData, mapAttendOpenHouseFormToApi } from './utils'
import { parseListingDetailsId } from '../../utils/routing/parse-format-details-slugged-values'
import { pushAnalyticsEvent, pushFBEvent } from '../../../analytics/push-analytics-event'
import { mapMutationErrorsForUI } from '../../../api-mappings/errors'
import { UserContext } from '../../../users/components/user-info-provider/user-context'

export interface AttendOpenHouseModalProps {
  isOpen: boolean
  onClose: () => void
  openHouseId: string
}

export function AttendOpenHouseModal({ isOpen, onClose, openHouseId }: AttendOpenHouseModalProps) {
  const { t } = useTranslation()
  const { query } = useRouter()
  const listingId = parseListingDetailsId(query['id'] as string)
  const { errorMessages } = useErrorMessages()
  const { user, userReady } = useContext(UserContext)
  const [formValues, setFormvalues] = useState<SchemaFormValues>({})
  const [submitted, setSubmitted] = useState<boolean>(false)

  // We need two sets of errors here because of the way the SchemaFormLite set errors on its passed-in errors.
  // We want a separate set of "ui errors" that are displayed independently at the top of the page.
  const [apiErrors, setApiErrors] = useState<FieldError[]>()
  const [uiErrors, setUiErrors] = useState<FieldError[]>([])

  const {
    mutate: attenOpenHouseMutation,
    isSuccess,
    reset,
    isError,
    error,
  } = useCreateResidentialListingOpenHouseInquiry()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!userReady) {
      return
    }

    setFormvalues({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phoneNumber: user?.primaryPhone || '',
      email: user?.email || '',
    })
  }, [user, userReady])

  useEffect(() => {
    if (isError) {
      setSubmitted(false)
      const mappedApiErrors = mapMutationErrorsForUI(error)
      setApiErrors(mappedApiErrors)
      setUiErrors(mappedApiErrors.map((err) => ({ invalidField: err.invalidField, errorMessage: err.errorMessage })))
    }

    if (isSuccess) {
      onClose()
      reset()
      setSubmitted(false)
      setUiErrors([])
      pushAnalyticsEvent('generate_lead')
      pushFBEvent('Lead')
      queryClient.invalidateQueries(['GetListingLite'])
    }
  }, [isError, error, isSuccess, onClose, reset, queryClient])

  function onSubmit(data: AttendOpenHouseFormData) {
    setSubmitted(true)
    if (listingId) {
      attenOpenHouseMutation(mapAttendOpenHouseFormToApi(data, openHouseId))
    }
  }

  function onError(errors: GraphQLError) {
    setSubmitted(false)
    const newErrors: FieldError[] = []

    Object.keys(errors).forEach((errorKey) => {
      newErrors.push({ invalidField: errorKey, errorMessage: errorMessages[errorKey] })
    })

    setUiErrors(newErrors)
  }

  function onCancel() {
    setSubmitted(false)
    setUiErrors([])
    setApiErrors([])
    reset()
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={t('listings:open-houses.attend')}
      contentSx={{
        padding: '0px',
        textAlign: 'start',
      }}
      maxWidth="smmd"
    >
      <div className="px-8 pt-8 pb-4">
        {/* TODO V2-1288: remove client side validation properly when there's time to refactor */}
        <ErrorContainer errors={uiErrors} className="mb-6 px-8" />
        <SchemaFormLite
          formId="attend-open-house-form"
          onSubmit={onSubmit}
          onError={onError}
          section={formSection}
          showTitle={false}
          showActionButton={false}
          values={formValues}
          errors={apiErrors}
          showErrors={false}
        />
      </div>
      <div className="flex items-center justify-between border-t border-shades-200 px-8 py-4">
        <Button type="reset" variant="ghost" onClick={onCancel}>
          {t('ui:schema-form.cancel')}
        </Button>
        {/* NOTE: Use a fixed width button here so that we don't get any resizing when showing the spinner. */}
        <Button form="attend-open-house-form" type="submit" variant="primary" className="w-24">
          {submitted ? <Spinner /> : t('ui:schema-form.submit')}
        </Button>
      </div>
    </Modal>
  )
}

export default AttendOpenHouseModal
